// Entrega 4 — «Vivo» (04-11-2026, web/qa/auditoria-apple/plan.md; flujo.md #7: 111 «¿ya?» en 35 días porque mientras
// trabaja no se ve nada vivo). Lo que un agente hace AHORA, en la fila de la lista y en la línea de encima de la caja:
// «Paso 7 · Comprobando los precios · hace 20 s».
//
// Una sola fuente: el chorro de mensajes del agente que la web ya tiene (`useAppStore.messagesByAgent`, alimentado por
// `agent:message`, que el servidor emite a todos para todos los agentes: websocket/index.ts). Nada nuevo en el servidor.
//   - paso   = cuántas herramientas ha usado en el turno en curso (las suyas, no las de sus ayudantes). Un turno empieza
//              con cada mensaje que le llega (de Pau o de la oficina) y acaba con el fin de turno del motor. Si el
//              principio del turno no está en lo que tiene la web (la página se abrió a mitad), el número no se sabe y
//              NO se inventa: se dice solo qué hace.
//   - texto  = la `description` que el modelo escribe en cada herramienta (founder-first §3: la señal gratis), tal cual,
//              si es apta para Pau: en español y sin rutas, comandos, nombres de archivo ni JSON (lenguaje.ts). Si no,
//              qué clase de cosa hace, en cristiano («Haciendo comprobaciones»).
//   - en     = cuándo empezó ese paso.
// El ritmo: un reloj compartido de 1 s; cada fila lee una foto (texto) y React solo repinta si la foto cambia. Así una
// ráfaga de 20 lecturas en medio segundo es UN repintado, y «hace N s» corre sin un temporizador por fila.
import { useCallback, useSyncExternalStore } from 'react';
import { t } from '../../../i18n';
import type { Message } from '../../../types';
import { asToolUse, SUBAGENT_TOOL_NAMES } from '../../../lib/messages';
import { useAppStore } from '../../../stores/app.store';
import { MINUTOS_SIN_NOTICIAS } from '../compartido/estado-visual';
import { detectarTecnicismos } from '../compartido/lenguaje';
import { hace } from '../marco/estado';
import { CAMBIAR, COMPROBAR, MIRAR, PREFIJO } from './linea-de-tiempo';

export interface PasoEnCurso {
  /** Número de herramientas del turno en curso; null si el turno empezó antes de lo que la web tiene. */
  paso: number | null;
  texto: string;
  /** ISO: cuándo empezó este paso. */
  en: string;
}

const obj = (c: unknown): Record<string, unknown> => (c && typeof c === 'object' ? (c as Record<string, unknown>) : {});
const deArriba = (m: Message) => !m.parentToolUseId;
/** Un mensaje guardado que aún no le ha llegado al agente no abre turno (sigue con lo de antes). */
const abreTurno = (m: Message) => m.type === 'user' && deArriba(m) && m.delivery?.state !== 'pending';
const cierraTurno = (m: Message) => m.type === 'system' && obj(m.content).subtype === 'result';

/**
 * Lo que hace ahora, del chorro de mensajes (puro). null si no está en mitad de un turno con alguna herramienta: su
 * turno acabó, aún no ha usado ninguna o no hay mensajes.
 */
export function pasoEnCurso(mensajes: readonly Message[]): PasoEnCurso | null {
  let ultimo: Message | null = null;
  let cuenta = 0;
  for (let i = mensajes.length - 1; i >= 0; i -= 1) {
    const m = mensajes[i]!;
    if (cierraTurno(m)) {
      // Su turno acabó después de su última herramienta: ahora no hace nada.
      if (!ultimo) return null;
      break;
    }
    if (abreTurno(m)) return ultimo ? { paso: cuenta, texto: textoDelPaso(ultimo), en: ultimo.timestamp } : null;
    if (m.type === 'tool_use' && deArriba(m)) {
      ultimo ??= m;
      cuenta += 1;
    }
  }
  // Sin el principio del turno (o con un fin de turno de por medio que no debería estar ahí): qué hace, sin número.
  return ultimo ? { paso: null, texto: textoDelPaso(ultimo), en: ultimo.timestamp } : null;
}

/** Herramientas de la oficina (mcp__oficina__*) con nombre propio en la línea. */
const DE_LA_OFICINA: Record<string, string> = {
  mandar_a_pau: 'escribiendote',
  reportar_estado: 'apuntando',
  pedir_permiso: 'permiso',
  pedir_decision: 'pregunta',
  proponer_contrato: 'plan',
  informe_de_cierre: 'informe',
  terminar: 'cerrando',
  ver_agentes: 'companeros',
};
const EN_INTERNET = new Set(['WebFetch', 'WebSearch']);

/** Cuando no hay descripción apta: qué clase de cosa hace (nunca el nombre técnico de la herramienta). */
export function etiquetaDeHerramienta(nombre: string): string {
  if (nombre.startsWith(PREFIJO)) {
    const propia = DE_LA_OFICINA[nombre.slice(PREFIJO.length)];
    return t(`oficinaV2.vivo.herramienta.${propia ?? 'oficina'}` as 'oficinaV2.vivo.herramienta.oficina');
  }
  if (EN_INTERNET.has(nombre)) return t('oficinaV2.vivo.herramienta.internet');
  if (MIRAR.has(nombre)) return t('oficinaV2.vivo.herramienta.mirar');
  if (CAMBIAR.has(nombre)) return t('oficinaV2.vivo.herramienta.cambiar');
  if (COMPROBAR.has(nombre)) return t('oficinaV2.vivo.herramienta.comprobar');
  if (SUBAGENT_TOOL_NAMES.has(nombre)) return t('oficinaV2.vivo.herramienta.ayudante');
  if (nombre === 'TodoWrite') return t('oficinaV2.vivo.herramienta.tareas');
  return t('oficinaV2.vivo.herramienta.otra');
}

const CONSOLA = /(&&|\|\||;|>|<|`|\$\(|^[[{]|^\/)/;
const MAX_DESCRIPCION = 120;
// Palabras muy frecuentes de cada idioma (como pareceIngles de lenguaje.ts, pero para frases de 3-6 palabras): el
// modelo escribe la descripción en el idioma en que piensa, que en esta flota suele ser el inglés.
const VACIAS_ES = /^(el|la|los|las|de|del|que|y|en|un|una|unos|unas|por|para|con|sin|lo|se|al|su|sus|si|más|qué|cómo|cuánto|cuántos|antes|después|otra|otro|todo|todos|todas)$/;
const VACIAS_EN = /^(the|and|of|to|for|with|in|on|from|an|is|are|all|into|check|run|get|read|list|find|show|update|create|fix|verify|wait|install|build|test|tests|search|look|open|start|stop|add|remove|write|edit|commit|push|fetch|view|inspect|locate|orient|review|compare|count|query|load|set|make)$/;
const GERUNDIO = /(ando|iendo|yendo)$/;

/** ¿Se puede enseñar tal cual a Pau? En español, corta y sin nada técnico. */
export function descripcionApta(texto: string): boolean {
  if (!texto || texto.length > MAX_DESCRIPCION || CONSOLA.test(texto)) return false;
  if (detectarTecnicismos(texto).length) return false;
  const palabras = texto.toLowerCase().match(/\p{L}+/gu) ?? [];
  let es = 0;
  let en = 0;
  for (const p of palabras) {
    if (VACIAS_EN.test(p)) en += 1;
    else if (VACIAS_ES.test(p) || GERUNDIO.test(p) || /[áéíóúñ¿¡]/.test(p)) es += 1;
  }
  return es > 0 && es >= en;
}

function textoDelPaso(m: Message): string {
  const tu = asToolUse(m);
  const crudo = typeof tu.input.description === 'string' ? tu.input.description.replace(/\s+/g, ' ').trim().replace(/[.:]$/, '') : '';
  if (crudo && descripcionApta(crudo)) return crudo.charAt(0).toUpperCase() + crudo.slice(1);
  return etiquetaDeHerramienta(tu.name);
}

// ─── Cómo se dice (puro, probado) ───

export interface LineaPaso {
  /** «Paso 7» o null (no se sabe el número). */
  paso: string | null;
  texto: string;
  /** «hace 20 s», «hace 3 min». */
  hace: string;
  /** Lleva en este paso tanto como «sin noticias» (15 min): se pinta en ámbar, como «sin noticias». */
  largo: boolean;
}

/** «hace 20 s» por debajo del minuto (vivo); luego como el resto de la oficina («hace 3 min»). */
export function haceVivo(iso: string, ahora: number): string {
  const s = Math.floor((ahora - Date.parse(iso)) / 1000);
  if (!Number.isFinite(s)) return '';
  if (s < 1) return t('oficinaV2.tiempo.ahora');
  if (s < 60) return t('oficinaV2.vivo.haceS', { n: s });
  return hace(iso, ahora);
}

export function lineaDePaso(p: PasoEnCurso, ahora: number): LineaPaso {
  return {
    paso: p.paso === null ? null : t('oficinaV2.vivo.paso', { n: p.paso }),
    texto: p.texto,
    hace: haceVivo(p.en, ahora),
    largo: ahora - Date.parse(p.en) >= MINUTOS_SIN_NOTICIAS * 60_000,
  };
}

/** La línea entera en texto: «Paso 7 · Comprobando los precios · hace 20 s» (para leer en voz alta y en las pruebas). */
export function fraseDePaso(l: LineaPaso): string {
  return [l.paso, l.texto, l.hace].filter(Boolean).join(' · ');
}

// ─── El reloj compartido y el gancho ───

const oyentes = new Set<() => void>();
let reloj: ReturnType<typeof setInterval> | null = null;
/** Cada cuánto se mira (y como mucho se repinta) una fila. */
export const RITMO_MS = 1000;
// La hora del último tic: entre dos tics, todas las fotos se calculan con la misma (useSyncExternalStore pide que leer
// dos veces seguidas dé lo mismo). Sin reloj en marcha (antes de la primera suscripción), la de ahora.
let marca = 0;
const ahoraDelReloj = () => (reloj ? marca : (marca = Date.now()));

function suscribir(oyente: () => void): () => void {
  oyentes.add(oyente);
  reloj ??= setInterval(() => {
    marca = Date.now();
    oyentes.forEach((o) => o());
  }, RITMO_MS);
  return () => {
    oyentes.delete(oyente);
    if (oyentes.size === 0 && reloj) {
      clearInterval(reloj);
      reloj = null;
    }
  };
}

// El cálculo, una vez por lista de mensajes (la lista cambia de objeto con cada mensaje nuevo).
const calculado = new WeakMap<readonly Message[], PasoEnCurso | null>();
function pasoDe(mensajes: readonly Message[] | undefined): PasoEnCurso | null {
  if (!mensajes) return null;
  if (!calculado.has(mensajes)) calculado.set(mensajes, pasoEnCurso(mensajes));
  return calculado.get(mensajes)!;
}

// La última foto de cada agente: si no cambia, se devuelve la misma (React no repinta).
const fotos = new Map<string, LineaPaso | null>();

/** Lo que hace ahora el agente (solo si `trabajando`), al ritmo del reloj compartido; null si no hay nada que decir. */
export function usePasoEnVivo(agentId: string, trabajando: boolean): LineaPaso | null {
  const foto = useCallback((): LineaPaso | null => {
    const p = trabajando ? pasoDe(useAppStore.getState().messagesByAgent[agentId]) : null;
    const nueva = p ? lineaDePaso(p, ahoraDelReloj()) : null;
    const vieja = fotos.get(agentId) ?? null;
    if (vieja === nueva || (vieja && nueva && fraseDePaso(vieja) === fraseDePaso(nueva) && vieja.largo === nueva.largo)) return vieja;
    fotos.set(agentId, nueva);
    return nueva;
  }, [agentId, trabajando]);
  return useSyncExternalStore(suscribir, foto, foto);
}
